import * as React from 'react';

import { enumUnit } from '@rematter/frontend-lib-api-client';
import { ContentCard } from '@rematter/ui-web';
import { Button, Money, MeasuredValue } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideDot } from 'lucide-react';

import { LoadLineItemRecord } from '../../_graphql/queryLoadDetails';
import * as state from '../../_state/loadReconciliation.state';

import { LoadReceivedMaterial } from './LoadReceivedMaterial';

export type LoadLineItemProps = {
  lineItem: LoadLineItemRecord;
};

export const LoadLineItem = ({ lineItem }: LoadLineItemProps) => {
  const addReceivedMaterial = useSetAtom(state.$addReceivedMaterial);
  const netWeightDelta = useAtomValue(state.$lineItemsNetWeightDelta);
  const totalValueDelta = useAtomValue(state.$lineItemsTotalValueDelta);
  const canEditReconciliation = useAtomValue(state.$canEditReconciliation);
  const receivedItemsByLineItem = useAtomValue(state.$receivedItemsByLineItem);

  const receivedItems = receivedItemsByLineItem[lineItem.id] ?? [];

  const netWeightData = lineItem.weights?.['net'];
  const netWeight = netWeightData?.value ?? 0;
  const netWeightUnit = netWeightData?.unit ?? enumUnit.Pounds;
  const unitPrice = lineItem.unitPrice?.amount ?? 0;
  // Calculate total received quantity from received items
  const totalReceivedQuantity = receivedItems.reduce(
    (sum, item) => sum + (item.netWeight?.value ?? 0),
    0,
  );
  const totalValue = totalReceivedQuantity * unitPrice;

  const skuNumber = lineItem.item?.skuNumber ?? '000';
  const itemName = lineItem.item?.name ?? 'Material Name Here';

  const netDelta = netWeightDelta[lineItem.id] ?? 0;
  const totalDelta = totalValueDelta[lineItem.id] ?? 0;
  const shouldShowDeltas = Boolean(
    receivedItems.length && (netDelta || totalDelta),
  );

  const handleAddReceivedMaterial = React.useCallback(() => {
    addReceivedMaterial({
      lineItemId: lineItem.id,
      pricingUnit: netWeightUnit,
    });
  }, [addReceivedMaterial, lineItem.id, netWeightUnit]);

  return (
    <ContentCard key={lineItem.id} className="@LoadLineItem">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          {/* 🟡🟡🟡 SKU & Item Name */}
          <div className="flex items-center gap-2 font-medium">
            <div className="rounded bg-neutral/10 px-1.5 py-0.5 text-sm">
              {skuNumber}
            </div>
            <div>{itemName}</div>
          </div>
          {/* 🟡🟡🟡 Buttons */}
          {canEditReconciliation && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                color="neutral"
                className="border-neutral/20"
                onClick={handleAddReceivedMaterial}
              >
                Add Received Material
              </Button>
            </div>
          )}
        </div>
        {/* 🟡🟡🟡 Net weight, unit price, total value */}
        <div className="flex items-center gap-1 text-sm text-neutral/60">
          <div>
            <span className="font-semibold">N</span>{' '}
            <MeasuredValue value={netWeight} unit={netWeightUnit} />
          </div>
          <LucideDot size={16} className="opacity-60" />
          <div>
            <Money
              amount={unitPrice}
              currencyCode={lineItem.unitPrice?.currencyCode}
            />
          </div>
          <LucideDot size={16} className="opacity-60" />
          <div>
            <Money
              amount={totalValue}
              currencyCode={lineItem.unitPrice?.currencyCode}
            />
          </div>
        </div>
        {/* 🟡🟡🟡 Received materials */}
        <div className="flex flex-col gap-2 pt-1.5">
          {Boolean(receivedItems?.length) &&
            receivedItems?.map((receivedItem) => (
              <LoadReceivedMaterial
                key={receivedItem.id}
                receivedMaterial={receivedItem}
                lineItem={lineItem}
              />
            ))}
          {shouldShowDeltas && (
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_50px] gap-2">
              <div className="col-span-3" />
              <div className="flex flex-col items-end px-2.5">
                <div className="text-[11px] text-neutral/40 uppercase">
                  Net Delta
                </div>
                <div className="text-sm font-semibold">
                  <MeasuredValue value={netDelta} unit={netWeightUnit} />
                </div>
              </div>
              <div />
              <div className="flex flex-col items-end px-2.5">
                <div className="text-[11px] text-neutral/40 uppercase">
                  Total Delta
                </div>
                <div className="text-sm font-semibold">
                  <Money amount={totalDelta} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ContentCard>
  );
};
